import PropTypes from 'prop-types'
import { formatRelativeTime } from '@utils'
import './NotificationItem.scss'

/**
 * NotificationItem Component
 *
 * Displays a single notification with icon, title, message, and timestamp.
 * Supports click action and visual distinction for unread items.
 */
const NotificationItem = ({
	notification,
	onClick,
	onMarkAsRead
}) => {
	const { id, type, title, message, link, priority, read_at, created_at } = notification
	const isUnread = !read_at

	// Get icon based on notification type
	const getTypeIcon = () => {
		const icons = {
			task_assigned: '📋',
			task_due_soon: '⏰',
			task_overdue: '🚨',
			approval_needed: '✅',
			task_approved: '✓',
			task_rejected: '✗',
			project_approved: '🎉',
			comment: '💬',
			subscription_expiring: '⚠️',
			new_company: '🏢',
			system: 'ℹ️',
			default: '🔔'
		}
		return icons[type] || icons.default
	}

	// Get priority indicator color
	const getPriorityClass = () => {
		const classes = {
			urgent: 'notification-item--urgent',
			high: 'notification-item--high',
			normal: '',
			low: 'notification-item--low'
		}
		return classes[priority] || ''
	}

	const handleClick = () => {
		if (isUnread && onMarkAsRead) {
			onMarkAsRead(id)
		}
		if (onClick) {
			onClick(notification)
		}
	}

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			handleClick()
		}
	}

	return (
		<div
			className={`notification-item ${isUnread ? 'notification-item--unread' : ''} ${getPriorityClass()}`}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			role="button"
			tabIndex={0}
			aria-label={`${isUnread ? 'Unread ' : ''}notification: ${title}`}
		>
			<div className="notification-item__icon">
				{getTypeIcon()}
			</div>

			<div className="notification-item__content">
				<div className="notification-item__header">
					<span className="notification-item__title">{title}</span>
					{isUnread && (
						<span className="notification-item__badge" aria-label="Unread"></span>
					)}
				</div>

				{message && (
					<p className="notification-item__message">{message}</p>
				)}

				<div className="notification-item__meta">
					<span className="notification-item__time">
						{formatRelativeTime(created_at)}
					</span>
					{priority === 'urgent' && (
						<span className="notification-item__priority">Urgent</span>
					)}
					{priority === 'high' && (
						<span className="notification-item__priority notification-item__priority--high">High Priority</span>
					)}
				</div>
			</div>

			{link && (
				<div className="notification-item__arrow">
					→
				</div>
			)}
		</div>
	)
}

NotificationItem.propTypes = {
	notification: PropTypes.shape({
		id: PropTypes.number.isRequired,
		type: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		message: PropTypes.string,
		link: PropTypes.string,
		priority: PropTypes.oneOf(['low', 'normal', 'high', 'urgent']),
		read_at: PropTypes.string,
		created_at: PropTypes.string.isRequired
	}).isRequired,
	onClick: PropTypes.func,
	onMarkAsRead: PropTypes.func
}

export default NotificationItem
